import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, ViewEncapsulation } from '@angular/core';
import { HxIcon } from '../icon/icon';

/**
 * Unified Dialog Component
 *
 * A single, consolidated component for creating dialogs with header, body, and footer sections. Replaces the need for
 * separate HxDialogHeaderComponent, HxDialogBodyComponent, and HxDialogFooterComponent.
 *
 * @example
 *   ```html
 *   <!-- Using input properties for header -->
 *   <hx-dialog [title]="'Confirm Action'" [icon]="'check-circle'">
 *     <div hxDialogContent>
 *       <p>Are you sure you want to proceed?</p>
 *     </div>
 *     <div hxDialogActions>
 *       <button hxButton variant="outline" (click)="onCancel()">Cancel</button>
 *       <button hxButton variant="fill" context="brand" (click)="onConfirm()">Confirm</button>
 *     </div>
 *   </hx-dialog>
 *   ```;
 *
 * @example
 *   ```html
 *   <!-- Using content projection for full control -->
 *   <hx-dialog>
 *     <div hxDialogHeader>
 *       <h2>Custom Header</h2>
 *     </div>
 *     <div hxDialogContent>
 *       <p>Dialog content...</p>
 *     </div>
 *     <div hxDialogActions>
 *       <button>Cancel</button>
 *       <button>Confirm</button>
 *     </div>
 *   </hx-dialog>
 *   ```;
 */
@Component({
  selector: 'hx-dialog',
  standalone: true,
  imports: [CommonModule, HxIcon],
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.css'],
  host: {
    class: 'hx-dialog',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class HxDialogComponent {
  /** Title text to display in header - optional, can be overridden by projected header content */
  title = input<string>();

  /** Optional icon name to display before the title */
  icon = input<string | undefined>();

  /** Optional color for the icon (CSS color string) */
  iconColor = input<string | undefined>();

  /** ID for aria-labelledby reference - defaults to 'hx-dialog-title' */
  headerId = input<string>('hx-dialog-title');

  /** ID for content aria-describedby reference - defaults to 'hx-dialog-content' */
  contentId = input<string>('hx-dialog-content');
}
